/* eslint-disable react/sort-comp */
import React from 'react';
import axios from 'axios';
import moment from 'moment';
import { notification } from 'antd';
import { connect } from 'react-redux';
import { FileExcelOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';

import config from '../config';

import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import CardGrid from '../common/cardGrid/cardGrid';
import FilterDrawer from '../common/filters/FilterDrawer';
import ProjectsCustomFilterTab from './ProjectsCustomFilterTab';
import ProjectColumnChanger from './ProjectColumnChanger';
import ProjectExportDrawer from './ProjectExportDrawer';

import Permissions from '../auth/Permissions';

import ProjectAddView from './projectAddView';
import ProjectItemView from './ProjectItemView';
import ProjectUserList from './ProjectUserList';
import exportExcel, { exportProjectsToExcel } from './projectExport';
import { getDisplayColumns, FIELDS_BY_EDIT_STEP } from './projects.constants';

import {
  createProject,
  archiveProject,
  bulkArchiveProjects,
  updateProject,
  getProjects,
  deleteProject,
  bulkDeleteProjects,
  getProjectEquipment,
  getProjectLabels,
} from './state/projects.actions';
import {
  mergeProjectsAndCostcodes,
  generateCostcodePayload,
  condensePhasesAndCostcodes,
  generateEquipmentPayload,
  formatContractExpiration,
  prepareProjectPayload,
  getCustomEstimateMaps,
  getBucketTemplateProjectMap,
} from './projectHelpers';

import { getAllPhases } from '../costcodes/state/costcodes.actions';

import { setMenu } from '../menu/state/menu.actions';
import TimeTracking from '../state/timeTracking';

import Clock from '../clock/Clock';

import UserClockInModal from '../users/UserClockInModal';

import { projectCardDimensions } from '../constants/Styles';
import sortByString, { getIdMap, isNullOrUndefined } from '../helpers/helpers';

import { createNuxEntry } from '../nux/state/nux.actions';
import { getEquipment, getEquipmentCustomDataMap } from '../equipment/state/equipment.actions';
import { getCompanyCustomizationTabs, getCompanyImageURL } from '../settings/state/settings.actions';
import { decoratePayloadWithLabels } from '../helpers/labels';
import { getCustomerLabels, getCustomers } from '../contacts/customers/state/customers.actions';
import { getBoards } from '../boards/state/boards.actions';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import { counters } from '../helpers/counters';
import { getLabels } from '../labels/state/labels.actions';
import { request } from '../helpers/requests';

const PROJECT_COSTCODE_COLUMNS = [
  'description',
  'hourlyWage',
  'dailyWage',
  'wageAdjustment',
  'wageMultiplier',
  'hourlyBillingRate',
  'dailyBillingRate',
];

const loadProjectCostcodes = async (projectId) => {
  if (!projectId) return [];
  const res = await request({
    call: axios.post('/costcodes/projects', {
      projectIds: [projectId],
      includeCategoryIds: true,
      columns: PROJECT_COSTCODE_COLUMNS,
    }),
    errMsg: 'Could not get project cost codes',
    hideSuccessToast: true,
  });
  if (!res || res.err) return null;
  return res.data ?? [];
};

class ProjectList extends React.Component {
  constructor(props) {
    super(props);

    this.cardGridRef = React.createRef();
    this.editCounter = 0;

    const {
      clockin,
      clockout,
      switchTask,
      takeBreak,
      endBreak,
      manualEntry,
      buckets,
      bucketTemplates,
    } = props;

    this.clock = new Clock({
      clockin,
      clockout,
      switchTask,
      takeBreak,
      endBreak,
      manualEntry,
    });

    // Cost codes are only needed once a project is opened, so they are loaded per
    // project rather than for every project in the list.
    this.costcodeRequests = new Map();

    this.onProjectOpened = ({ id } = {}) => this.ensureProjectCostcodes(id);

    this.state = {
      sortActive: window.localStorage.getItem('projectsSortText') || 'Code: Ascending',
      costcodesByProject: {},
      showUserList: false,
      showFiles: false,
      fullProjects: [],
      errors: {},
      customFields: [],
      loadingProjects: new Set(),
      fileSelected: false,
      filterDrawerVisible: false,
      exportDrawerVisible: false,
      selectedColumns: null,
      projectDrawerActiveTab: 'general',
      bucketMap: getIdMap(buckets),
      bucketTemplateProjectMap: getBucketTemplateProjectMap({
        bucketTemplates,
        buckets,
      }),
    };
    this.onValuesChanged = this.valuesChanged.bind(this);
    this.onShowFilesChanged = this.toggleShowFiles.bind(this);
    this.values = {};

    this.currentSort = JSON.parse(window.localStorage.getItem('projectsSortActive') ?? null);
    this.sortByName = this.sortName.bind(this);
    this.sortByCode = this.sortCode.bind(this);

    this.sortOptions = [{
      title: `Code 1 ${String.fromCharCode(8594)} 9`,
      action: () => this.sortByCode(true),
    }, {
      title: `Code 9 ${String.fromCharCode(8594)} 1`,
      action: () => this.sortByCode(false),
    }, {
      title: `Name A ${String.fromCharCode(8594)} Z`,
      action: () => this.sortName(true),
    }, {
      title: `Name Z ${String.fromCharCode(8594)} A`,
      action: () => this.sortName(false),
    }];

    this.onArchive = this.archiveProject.bind(this);
    this.onDelete = this.deleteProject.bind(this);
    this.onEdit = this.editProject.bind(this);
    this.onUserStatClick = this.showUserStats.bind(this);
    this.onEstimatedCostClick = this.showEstimatedCost.bind(this);
    this.onRunningCostClick = this.showRunningCost.bind(this);

    this.onUserListClose = this.closeUserList.bind(this);
    this.onUserClick = this.userClicked.bind(this);
    this.onUserClockClose = this.closeUserClock.bind(this);
    this.handleFileSelected = this.handleFileSelected.bind(this);

    this.onProjectAdd = this.addProject.bind(this);
    this.onValidateCustomFields = this.validateCustomFields.bind(this);
    this.onToggleFilterDrawer = this.toggleFilterDrawer.bind(this);
    this.onCloseFilterDrawer = this.closeFilterDrawer.bind(this);
    this.onColumnChange = this.columnChange.bind(this);
    this.toggleExportDrawer = this.toggleExportDrawer.bind(this);
    this.handleProjectExport = this.handleProjectExport.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      users: prevUsers,
      phases: prevPhases,
      selectedDivisions: prevSelected,
      projects: prevProjects,
      equipment: prevEquipment,
      projectTypes: prevProjectTypes,
      type: prevType,
      timeEntryUserMap: prevTimeEntries,
      categoryAssignments: prevCategoryAssignments,
      buckets: prevBuckets,
      bucketTemplates: prevBucketTemplates,
    } = prevProps;
    const { costcodesByProject: prevCostcodesByProject } = prevState;
    const { costcodesByProject } = this.state;
    const {
      projects,
      users,
      positions = {},
      teams,
      phases = [],
      selectedDivisions = new Set(),
      projectEquipment,
      equipment,
      company: {
        settings = {},
      } = {},
      projectTypes,
      type,
      timeEntryUserMap,
      roundingPolicyPositionMap,
      categoryAssignments,
      buckets,
      bucketTemplates,
    } = this.props;

    if (buckets.length > 0 && prevBuckets !== buckets) {
      this.setState({
        bucketMap: getIdMap(buckets),
      });
    }

    if (
      prevBucketTemplates !== bucketTemplates
      || prevBuckets !== buckets
    ) {
      this.setState({
        bucketTemplateProjectMap: getBucketTemplateProjectMap({
          bucketTemplates,
          buckets,
        }),
      });
    }

    if (this.props.location?.state?.targetId && this.props.location?.state?.targetId !== prevProps.location?.state?.targetId) {
      this.openProjectFromLocationState();
    }

    this.clock.users = users;
    this.clock.settings = settings;
    this.clock.positions = positions;
    this.clock.roundingPolicyPositionMap = roundingPolicyPositionMap;

    if (
      this.shouldReload
      || costcodesByProject !== prevCostcodesByProject
      || (users.length > 0 && users !== prevUsers)
      || (projects !== prevProjects)
      || (phases.length > 0 && prevPhases !== phases)
      || (equipment.length > 0 && prevEquipment !== equipment)
      || (selectedDivisions.size > 0 && selectedDivisions !== prevSelected)
      || (projectTypes.length > 0 && projectTypes !== prevProjectTypes)
      || timeEntryUserMap !== prevTimeEntries
      || type !== prevType
      || categoryAssignments !== prevCategoryAssignments
    ) {
      this.shouldReload = false;

      let divProjects = projects;
      if (config.showDivisions) {
        divProjects = projects.filter((project) => selectedDivisions.has(project.divisionId));
      }
      const fullProjects = mergeProjectsAndCostcodes({
        timeEntryUserMap,
        projects: divProjects,
        costcodes: Object.values(costcodesByProject).flat(),
        users,
        teams,
        phases,
        projectEquipment,
        equipment,
        categoryAssignments,
      }).map((proj) => ({
        ...proj,
        onUserStatClick: this.onUserStatClick,
        onEstimatedCostClick: this.onEstimatedCostClick,
        onRunningCostClick: this.onRunningCostClick,
      }));

      this.projectMap = getIdMap(fullProjects);
      this.setState({
        fullProjects,
      }, () => {
        if (this.currentSort) {
          const [sortType, sortAscending] = this.currentSort;
          const sortFunc = sortType === 'name' ? this.sortByName : this.sortByCode;
          sortFunc(sortAscending);
        } else {
          this.sortByCode(true);
        }

        this.openProjectFromLocationState(prevProps);
      });
    }
  }

  async componentDidMount() {
    const {
      getCompanyCustomizationTabs,
      getCompanyImageURL,
      getBoards,
      getTimeEntries,
    } = this.props;

    await Promise.all([
      getCompanyCustomizationTabs(),
      getCompanyImageURL(),
      getBoards(),
      getTimeEntries({
        startTime: DateTime.now().startOf('day').toMillis(),
        endTime: DateTime.now().endOf('day').toMillis(),
        getRunningTasks: true,
        getCardDetails: true,
      }),
    ]);
  }

  // Loads one project's cost codes the first time that project is opened.
  // Concurrent callers share the same in-flight request.
  ensureProjectCostcodes(projectId, force = false) {
    if (!projectId) return Promise.resolve([]);
    if (force) this.costcodeRequests.delete(projectId);
    if (this.costcodeRequests.has(projectId)) return this.costcodeRequests.get(projectId);

    const pending = loadProjectCostcodes(projectId).then((costcodes) => {
      if (costcodes === null) {
        // Drop the cache entry so a later open retries instead of treating the
        // failure as "this project has no cost codes".
        this.costcodeRequests.delete(projectId);
        return null;
      }
      this.setState((prevState) => ({
        costcodesByProject: {
          ...prevState.costcodesByProject,
          [projectId]: costcodes,
        },
      }));
      return costcodes;
    });

    this.costcodeRequests.set(projectId, pending);
    return pending;
  }

  async reloadData(id) {
    const {
      getProjects,
      getAllPhases,
      getEquipment,
      getProjectEquipment,
      getCustomers,
      getCustomerLabels,
      getProjectLabels,
      getLabels,
    } = this.props;
    const {
      loadingProjects = new Set(),
    } = this.state;
    await Promise.all([
      getProjects(),
      getAllPhases(),
      getProjectEquipment(true),
      getEquipment(true),
      getCustomers(true),
      getCustomerLabels(true),
      getProjectLabels(),
      getLabels(),
    ]);
    await this.ensureProjectCostcodes(id, true);
    const newLoadingSet = new Set(Array.from(loadingProjects));
    newLoadingSet.delete(id);
    this.setState({ loadingProjects: newLoadingSet });
    this.shouldReload = true;
  }

  openProjectFromLocationState() {
    const {
      location: {
        state: {
          targetId,
          activeTab,
        } = {},
      } = {},
      history,
      pathname,
    } = this.props;

    const {
      fullProjects = [],
    } = this.state;

    if (!fullProjects) return;
    const locationStateProject = fullProjects.find((project) => project.id === targetId);
    if (locationStateProject && this.cardGridRef) {
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => {
        this.cardGridRef.displayItem({
          ...locationStateProject,
          active: !!locationStateProject.active,
          activeTab: activeTab || 'general',
        });
      }, 250);
      history?.replace(pathname); // Clear location state
    }
  }

  valuesChanged(key, value) {
    this.values[key] = value;
  }

  toggleShowFiles(showFiles) {
    this.setState({
      showFiles,
    });
  }

  async addProject(values) {
    this.setState({ projectDrawerActiveTab: 'general' });
    const {
      createProject,
      divisions = {},
    } = this.props;
    const {
      showFiles,
      customFields,
    } = this.state;

    const {
      payload,
      ourFileMap,
      divisionName,
    } = prepareProjectPayload({
      formValues: values,
      storedValues: this.values,
      showFiles,
      divisions,
      customFields,
    });

    const success = await createProject(payload, ourFileMap, divisionName);

    if (success) {
      this.setState({ loadingProjects: new Set([success?.id]) });
      this.reloadData(success?.id); // Sledgehammer
      this.values = {};
    }
    return success;
  }

  async editProject(id, details) {
    this.setState({ projectDrawerActiveTab: 'general' });
    const {
      phases: propPhases = [],
      t,
      updateProject,
      projectToLabel,
    } = this.props;
    const {
      fullProjects = [],
      customFields = [],
    } = this.state;
    const {
      costcodes,
      phases,
      geofence,
      budget = {},
      equipment = [],
      equipmentSchedule = {},
      projectTypeId,
      useCoordinates,
    } = this.values;

    const finalPayload = {};

    const relevantProjects = fullProjects.filter((project) => project.id === id);
    if (relevantProjects.length !== 1) {
      Sentry.withScope(() => {
        Sentry.captureException(
          new Error(`Updating for project with id ${id}. Project not found in props`),
        );
      });
      return;
    }

    const oldPhases = propPhases.filter(({ projectId }) => projectId === id);
    // Awaited so a save that races the drawer-open fetch still diffs against real
    // data. Bail rather than diff against nothing: an empty oldCostcodes turns every
    // existing code into a re-link and drops edits and removals.
    const fetchedCostcodes = await this.ensureProjectCostcodes(id);
    if (fetchedCostcodes === null) {
      Sentry.withScope(() => {
        Sentry.captureException(
          new Error(`Could not load cost codes for project ${id}. Aborting save.`),
        );
      });
      return;
    }
    const oldPhaseCondensed = condensePhasesAndCostcodes(oldPhases, fetchedCostcodes);
    const ourProject = relevantProjects[0];

    // Matches what mergeProjectsAndCostcodes builds, but off the value just awaited
    // rather than off fullProjects, which lags the fetch by two render cycles.
    const oldProjectCostcodes = fetchedCostcodes
      .filter((cc) => cc.projectId === id)
      .map((cc) => ({
        ...cc,
        categoryAssignments: this.props.categoryAssignments?.[cc.id]?.unphased ?? {},
      }));

    const data = generateCostcodePayload({
      newCostcodes: costcodes,
      oldCostcodes: oldProjectCostcodes,
      newPhases: phases,
      oldPhases: oldPhaseCondensed,
    });

    const equipmentPayload = generateEquipmentPayload({
      newEquipment: equipment,
      oldEquipment: ourProject.equipment,
      equipmentSchedule,
    });

    const ourDetails = formatContractExpiration({
      ...details,
      ...budget,
      projectTypeId,
    });

    // Extract all categoryEstimate values and remove from ourDetails
    const customCategoryEstimatesMap = getCustomEstimateMaps(ourDetails, 'categoryEstimate.');
    const customCategoryEstimates = [];
    Object.entries(customCategoryEstimatesMap).forEach(([categoryId, value]) => {
      if (!isNullOrUndefined(value)) {
        customCategoryEstimates.push({ categoryId, value });
      }
      delete ourDetails[`categoryEstimate.${categoryId}`];
    });

    const projectDetails = {};
    Object.keys(ourDetails).forEach((key) => {
      if (ourDetails[key] !== undefined) {
        let existingKey = key;
        if (key === 'estimatedLabourCost') {
          existingKey = 'labourCost';
        } else if (key === 'estimatedLabourHours') {
          existingKey = 'labourHours';
        }
        if (ourProject[existingKey] !== ourDetails[key]) {
          let val = ourDetails[key];
          if (key === 'billingContact' && val === '') val = null;
          projectDetails[key] = val && typeof val === 'string'
            ? val.trim()
            : val;
        }
      } else if (key === 'customerId' && ourProject.customerId !== undefined) {
        // This will happen if user clears the customer field
        projectDetails.customerId = null;
      }
    });

    if (projectDetails.materialDiscount) {
      projectDetails.materialDiscount /= 100;
    }

    if (projectDetails.customer) {
      delete projectDetails.customerId;
      delete projectDetails.customer.id;
      projectDetails.customer = decoratePayloadWithLabels(projectDetails.customer);
    } else {
      delete projectDetails.customer;
    }

    // Handle project labels using the helper function
    if (ourDetails.labels) {
      const decoratedPayload = decoratePayloadWithLabels(
        { labels: ourDetails.labels },
        id,
        projectToLabel,
      );

      if (decoratedPayload.addedLabels) {
        finalPayload.addedLabels = decoratedPayload.addedLabels;
      }
      if (decoratedPayload.removedLabels) {
        finalPayload.removedLabels = decoratedPayload.removedLabels;
      }

      delete projectDetails.labels;
    }

    if (ourDetails.dateRange && ourDetails.dateRange.length === 2) {
      delete projectDetails.dateRange;
      const [startDate, endDate] = ourDetails.dateRange;
      projectDetails.startDate = moment.isMoment(startDate) ? startDate.format('YYYY-MM-DD') : null;
      projectDetails.endDate = moment.isMoment(endDate) ? endDate.format('YYYY-MM-DD') : null;
    }

    if (geofence) {
      projectDetails.geofence = geofence;
    } else if ('geofence' in this.values) {
      // Someone removed
      projectDetails.geofence = null;
    }

    if (useCoordinates) {
      projectDetails.useCoordinates = useCoordinates;
    } else if ('useCoordinates' in this.values) {
      projectDetails.useCoordinates = false;
    }

    if (Object.keys(projectDetails).length > 0) {
      finalPayload.details = projectDetails;
    }

    Object.keys(data).forEach((key) => {
      if (data[key].length > 0) {
        finalPayload[key] = data[key];
      }
    });

    Object.keys(equipmentPayload).forEach((key) => {
      if (equipmentPayload[key].length > 0) {
        finalPayload[key] = equipmentPayload[key];
      }
    });

    const detailsPayload = formatCustomFieldCreatePayload({
      payload: finalPayload?.details,
      customFields,
    });

    if (customCategoryEstimates.length > 0) {
      detailsPayload.customCategoryEstimates = customCategoryEstimates;
    }

    if (!detailsPayload?.error) {
      finalPayload.details = detailsPayload;
    }

    const success = await updateProject(id, finalPayload);
    if (success) {
      // Reset the counters to force fresh data fetch
      if (counters.projects) counters.projects.getProjects = null;
      const notificationKey = `ProjectSaveNotification-${id}`;
      notification.open({
        key: notificationKey,
        message: `Saving ${t('Project')}`,
        description:
          `Click here to save a snapshot of the previous ${t('Project').toLowerCase()} details`,
        onClick: () => {
          exportExcel({
            ...ourProject,
            t,
          });
          notification.close(notificationKey);
        },
        duration: 5,
        icon: <FileExcelOutlined/>,
      });
      this.setState({ loadingProjects: new Set([id]) });
      this.reloadData(id); // Sledgehammer
      this.values = {};
      return true;
    }
    return false;
  }

  archiveProject(project) {
    const {
      t,
      archiveProject: archiveFunc,
    } = this.props;
    const {
      loadingProjects,
    } = this.state;
    const self = this;
    return new Promise((resolve) => {
      const mode = project.active ? 'Archive' : 'Activate';
      CustomConfirmModal({
        title: `${mode} ${t('Project').toLowerCase()} ${project.name}?`,
        okText: mode,
        cancelText: 'Cancel',
        async onOk() {
          self.setState({
            loadingProjects: new Set([project.id]),
          });
          await archiveFunc(project.id, !project.active);

          const newLoadingSet = new Set(Array.from(loadingProjects));
          newLoadingSet.delete(project.id);
          self.setState({
            loadingProjects: newLoadingSet,
          });
          resolve();
        },
        onCancel() {
          resolve();
        },
      });
    });
  }

  deleteProject(project) {
    return new Promise((resolve) => {
      const {
        deleteProject,
        t,
      } = this.props;
      CustomConfirmModal({
        title: `Permanently delete ${t('Project').toLowerCase()} ${project.name}?`,
        okText: 'Delete',
        cancelText: 'Cancel',
        async onOk() {
          resolve(deleteProject(project.id));
        },
        onCancel() {
          resolve();
        },
      });
    });
  }

  onBulkArchive = async (ids, { password }) => {
    const { bulkArchiveProjects: bulkArchive } = this.props;
    return bulkArchive(ids, { password }, false);
  }

  onBulkActivate = async (ids, { password }) => {
    const { bulkArchiveProjects: bulkArchive } = this.props;
    return bulkArchive(ids, { password }, true);
  }

  onBulkDelete = async (ids, { password }) => {
    const { bulkDeleteProjects: bulkDelete } = this.props;
    return bulkDelete(ids, { password });
  }

  getSort() {
    return {
      sortOptions: this.sortOptions,
      sortActive: this.state.sortActive,
    };
  }

  sortName(ascending) {
    const { fullProjects = [] } = this.state;
    const sortTitle = this.sortOptions[ascending ? 2 : 3].title;
    const currentSort = ['name', ascending];
    this.currentSort = currentSort;
    const sortedProjects = [...fullProjects];
    sortedProjects.sort((a, b) => (ascending ? 1 : -1) * sortByString('name')(a, b));
    this.setState({
      sortActive: sortTitle,
      fullProjects: sortedProjects,
    });
    window.localStorage.setItem('projectsSortText', sortTitle);
    window.localStorage.setItem('projectsSortActive', JSON.stringify(currentSort));
  }

  sortCode(ascending) {
    const { fullProjects = [] } = this.state;
    const sortTitle = this.sortOptions[ascending ? 0 : 1].title;
    const currentSort = ['code', ascending];
    this.currentSort = currentSort;
    const sortedProjects = [...fullProjects];
    sortedProjects.sort((a, b) => {
      if (isNaN(a.number)) return ascending ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      if (isNaN(b.number)) return ascending ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      return (a.number - b.number) * (ascending ? 1 : -1);
    });
    this.setState({
      sortActive: sortTitle,
      fullProjects: sortedProjects,
    });
    window.localStorage.setItem('projectsSortText', sortTitle);
    window.localStorage.setItem('projectsSortActive', JSON.stringify(currentSort));
  }

  showEstimatedCost(projectId) {
    if (this.cardGridRef) {
      this.cardGridRef.displayItem({
        ...this.projectMap[projectId],
        activeTab: '3',
        currentStep: 2,
      });
    }
  }

  showRunningCost() {
    this.props.history.push('/reporting');
    this.props.setMenu(['reporting']);
  }

  showUserStats(projectId) {
    this.setState({
      showUserList: true,
      selectedProject: this.projectMap[projectId],
    });
  }

  closeUserList() {
    this.setState({
      showUserList: false,
    });
  }

  userClicked(user) {
    this.setState({
      clockInUser: user,
      showClockIn: true,
    });
    this.clock.setUser(user);
  }

  closeUserClock() {
    this.setState({
      showClockIn: false,
    });
    setTimeout(() => {
      this.setState({
        clockInUser: null,
      });
      this.clock.setUser(null);
    }, 250);
  }

  validateCustomFields(currentStep, values) {
    if (currentStep !== 0) return;

    const { customFields } = this.state;
    const payload = formatCustomFieldCreatePayload({
      payload: values,
      customFields,
    });

    if (payload?.error || !payload) {
      this.setState({
        errors: payload?.errorMap,
      });

      throw new Error('Validation error with custom fields');
    }

    this.setState({
      errors: {},
    });
  }

  handleFileSelected(isSelected) {
    this.setState({ fileSelected: isSelected });
  }

  toggleFilterDrawer() {
    this.setState((prevState) => ({
      filterDrawerVisible: !prevState.filterDrawerVisible,
    }));
  }

  closeFilterDrawer() {
    this.setState({
      filterDrawerVisible: false,
    });
  }

  columnChange(newColumns) {
    this.setState({
      selectedColumns: newColumns ? new Set(newColumns) : null,
    });
  }

  toggleExportDrawer() {
    this.setState((prevState) => ({
      exportDrawerVisible: !prevState.exportDrawerVisible,
    }));
  }

  async handleProjectExport(exportOptions) {
    const {
      fullProjects = [],
      bucketTemplateProjectMap = {},
      bucketMap = {},
    } = this.state;
    const {
      divisions = {},
      projectToLabel = {},
      labels = [],
      vendors = {},
      buckets = [],
      equipmentTypes = [],
      getEquipmentCustomDataMap,
      company: {
        settings = {},
      } = {},
    } = this.props;

    const {
      projectColumnKeys = [],
      includeEquipment = false,
      equipmentColumnKeys = [],
    } = exportOptions;

    await exportProjectsToExcel({
      projects: fullProjects,
      columnKeys: projectColumnKeys,
      includeEquipment,
      equipmentColumnKeys,
      divisions,
      projectToLabel,
      labels,
      vendors,
      buckets,
      bucketMap,
      bucketTemplateProjectMap,
      equipmentTypes,
      getEquipmentCustomDataMap,
      settings,
    });
    notification.success({ message: 'Export complete' });
  }

  render() {
    const {
      company: {
        settings = {},
        companyImageURL = '',
      } = {},
      users,
      positionNames,
      approverPositions = new Set(),
      permittedProjects = [],
      createNuxEntry,
      nux = new Set(),
      history,
      divisions = {},
      cardLinks,
      tabs,
      t,
      projectTypes,
      type,
      useRedesign,
      labels,
      projectToLabel,
      projectFilterViews,
      onFavourite,
      projectFilters,
      filterActive,
      bucketTemplates = [],
      buckets = [],
      equipmentTypes = [],
    } = this.props;

    const {
      showUserList,
      selectedProject,
      clockInUser = {},
      showClockIn,
      showFiles,
      errors = {},
      customFields = [],
      loadingProjects = new Set(),
      fileSelected,
      fullProjects,
      selectedColumns,
      exportDrawerVisible,
      projectDrawerActiveTab,
      bucketMap,
      bucketTemplateProjectMap,
    } = this.state;

    // Projects are now pre-filtered from Projects.jsx

    const clockInUserId = clockInUser ? clockInUser.id : '';
    const sortData = this.getSort();

    const allDisplayColumns = getDisplayColumns({
      divisionMap: divisions,
      useRedesign,
      labels,
      projectToLabel,
      bucketMap,
      bucketTemplateProjectMap,
    });
    const displayColumns = selectedColumns
      ? allDisplayColumns.filter((col) => selectedColumns.has(col.dataIndex))
      : allDisplayColumns;

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <CardGrid
          onRef={(ref) => {
            this.cardGridRef = ref;
            this.openProjectFromLocationState();
          }}
          onItemOpened={this.onProjectOpened}
          dataSource={fullProjects}
          itemView={ProjectItemView}
          itemDimensions={projectCardDimensions}
          displayColumns={displayColumns}
          isListView
          colCount={4}
          search={{
             getField: (project) =>  {
              const fields = [];
              if (project.number) fields.push(project.number);
              if (project.name) fields.push(project.name);
              if (project.address) fields.push(project.address);
              if (project.city) fields.push(project.city);
              return fields.join(' ');
             },
          }}
          add={Permissions.has('PROJECTS_WRITE') ? {
            text: `Add a ${t('Project')}`,
            title: `Enter ${t('Project')} Details`,
            formView:(form,formProps) =>
              <ProjectAddView
                key={formProps.id}
                {...form}
                {...formProps}
                settings={settings}
                users={users}
                positionNames={positionNames}
                approverPositions={approverPositions}
                projects={permittedProjects}
                projectTypes={projectTypes}
                onValuesChanged={this.onValuesChanged}
                createNuxEntry={createNuxEntry}
                nux={nux}
                showFiles={showFiles}
                onShowFilesChanged={this.onShowFilesChanged}
                divisions={divisions}
                tabs={tabs}
                companyImageURL={companyImageURL}
                type={type}
                errors={errors}
                customFields={customFields}
                setCustomFields={(fields) => this.setState({ customFields: fields })}
                onFileSelectedChange={this.handleFileSelected}
                onActiveTabChange={(tab) => this.setState({ projectDrawerActiveTab: tab })}
              />,
            width: projectDrawerActiveTab === 'Cost Tracking'
              ? 2000
              : 1400,
            onClose: this.onProjectAdd,
            customValidator: this.onValidateCustomFields,
          } : null}
          edit={{
            zIndex: 890,
            title: `Edit ${t('Project')} Details`,
            getTitle: ({ name, editing }) => (editing ? `Edit ${name}` : name),
            push: false,
            formView: (form,formProps) => {
              const liveProject = this.projectMap?.[formProps.id] || {};
              return (
                <ProjectAddView
                  key={`${formProps.id}-edit-${this.editCounter++}`}
                  {...form}
                  {...formProps}
                  projectCostcodes={liveProject.projectCostcodes ?? formProps.projectCostcodes}
                  phases={liveProject.phases ?? formProps.phases}
                  settings={settings}
                  users={users}
                  // TODO: figure out what this is used for and if we need to pass it down
                  // Seems to be used for getting highest number
                  projects={permittedProjects}
                  projectTypes={projectTypes}
                  approverPositions={approverPositions}
                  positionNames={positionNames}
                  onValuesChanged={this.onValuesChanged}
                  createNuxEntry={createNuxEntry}
                  nux={nux}
                  history={history}
                  divisions={divisions}
                  cardLinks={cardLinks}
                  tabs={tabs}
                  companyImageURL={companyImageURL}
                  type={type}
                  errors={errors}
                  customFields={customFields}
                  setCustomFields={(fields) => this.setState({ customFields: fields })}
                  onFileSelectedChange={this.handleFileSelected}
                  onActiveTabChange={(tab) => this.setState({ projectDrawerActiveTab: tab })}
                />
              );
            },
            width: projectDrawerActiveTab === 'Cost Tracking'
              ? 2000
              : 1400,
            className: projectDrawerActiveTab === 'Cost Tracking'
              ? 'project-edit-drawer-cost-tracking'
              : 'project-edit-drawer',
            onClose: this.onEdit,
            canEdit: () => Permissions.has('PROJECTS_WRITE'),
            canArchive: (editProps) => (
              !loadingProjects.has(editProps?.id) && Permissions.has('PROJECTS_WRITE')
            ),
            canDelete: () => Permissions.has('PROJECTS_WRITE'),
            validateSteps: FIELDS_BY_EDIT_STEP,
            customValidator: this.onValidateCustomFields,
          }}
          onArchive={this.onArchive}
          onDelete={this.onDelete}
          bulkActions={Permissions.has('PROJECTS_WRITE') ? {
            canArchive: true,
            canActivate: true,
            canDelete: true,
            entityLabel: `${t('Project').toLowerCase()}s`,
          } : null}
          onBulkArchive={this.onBulkArchive}
          onBulkActivate={this.onBulkActivate}
          onBulkDelete={this.onBulkDelete}
          sort={sortData}
          filterActive={filterActive}
          onFilterOpen={this.onToggleFilterDrawer}
          loadingIds={loadingProjects}
          fileSelected={fileSelected}
          title={t('Projects')}
          entity={t('Project')}
          extra={(
            <ProjectColumnChanger
              onChange={this.onColumnChange}
            />
          )}
          more={[
            {
              title: 'Export to Excel',
              icon: 'file-excel',
              onClick: this.toggleExportDrawer,
            },
          ]}
        />
        <ProjectUserList
          visible={showUserList}
          onClose={this.onUserListClose}
          project={selectedProject}
          onUserClick={this.onUserClick}
        />
        <UserClockInModal
          key={clockInUserId}
          visible={showClockIn}
          user={clockInUser}
          projects={this.props.projects}
          unions={this.props.unions}
          onCloseClicked={this.onUserClockClose}
          onClockIn={this.clock.onClockIn}
          onClockOut={this.clock.onClockOut}
          onSwitch={this.clock.onSwitch}
          onBreakStart={this.clock.onBreakStart}
          onBreakEnd={this.clock.onBreakEnd}
        />
        <FilterDrawer
          visible={this.state.filterDrawerVisible}
          onClose={this.onCloseFilterDrawer}
          filterViews={projectFilterViews}
          CustomFilterTab={ProjectsCustomFilterTab}
          projectTypes={projectTypes}
          labels={labels}
          activeFilters={projectFilters}
          usePropFilters
          currentProjectTypeId={type}
          onFavourite={onFavourite}
        />
        <ProjectExportDrawer
          visible={exportDrawerVisible}
          onClose={this.toggleExportDrawer}
          onExport={this.handleProjectExport}
          equipmentTypes={equipmentTypes}
          bucketTemplateProjectMap={bucketTemplateProjectMap}
        />
      </div>
    );
  }
}

export default withTranslation()(
  connect((state, ownProps) => {
    return { ...ownProps,
      projectEquipment: state.equipment.projectEquipment,
      users: state.users.users,
      positions: state.settings.positions,
      teams: state.teams.teams,
      company: state.settings.company,
      phases: state.costcodes.phases,
      positionNames: state.settings.positionNames,
      approverPositions: state.settings.approverPositions,
      nux: state.nux.nux,
      selectedDivisions: state.settings.selectedDivisions,
      divisions: state.settings.divisions,
      equipment: state.equipment.equipment,
      equipmentTypes: state.equipment.equipmentTypes,
      unions: state.unions,
      cardLinks: state.boards.cardLinks,
      tabs: state.settings.tabs,
      projectTypes: state.projects.projectTypes,
      timeEntryUserMap: state.timeTracking.timeEntryUserMap,
      roundingPolicyPositionMap: state.settings.roundingPolicyPositionMap,
      projectToLabel: state.projects.projectToLabel,
      useRedesign: state.settings.company?.settings?.uxRedesignPt1,
      categoryAssignments: state.costcodes.categoryAssignments,
      bucketTemplates: state.buckets.bucketTemplates,
      buckets: state.buckets.buckets,
      vendors: state.vendors.vendors,
    };
  }, {
    createProject,
    archiveProject,
    bulkArchiveProjects,
    updateProject,
    deleteProject,
    bulkDeleteProjects,
    getProjects,
    getAllPhases,
    setMenu,
    clockin: TimeTracking.clockIn,
    clockout: TimeTracking.clockOut,
    switchTask: TimeTracking.switchTask,
    takeBreak: TimeTracking.startBreak,
    endBreak: TimeTracking.endBreak,
    manualEntry: TimeTracking.manualEntry,
    createNuxEntry,
    getProjectEquipment,
    getEquipment,
    getEquipmentCustomDataMap,
    getCompanyCustomizationTabs,
    getCompanyImageURL,
    getCustomers,
    getCustomerLabels,
    getBoards,
    getTimeEntries: TimeTracking.getTimeEntries,
    getProjectLabels,
    getLabels,
  })(withRouter(ProjectList)),
);
